import { useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { useTranslation } from '@/lib/i18n';
import { Eyebrow } from '../primitives';

export type RecentProject = {
	id: string;
	name: string;
	coverImage: string;
	economicGoal?: number;
	collectedCents: number;
	ngo?: { id: string; name?: string; logo?: { location: string } };
};

/**
 * Deliberately not a link. The card used to carry a "See more" pointing at `#`, which reads as a
 * broken promise; and there is no public project page to point it at — the nearest real destination
 * is the campaign on a merchant portal, which is a different page than the project. The card stays
 * informational until the new site has a project detail view designed.
 */
function ProjectCard({ project }: { project: RecentProject }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const raised = Math.round(project.collectedCents / 100);
	const goal = project.economicGoal;
	const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : undefined;

	return (
		<article className="flex w-[340px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl bg-white shadow-[0_8px_24px_0_rgba(77,33,0,0.06)]">
			<div className="relative aspect-[16/10] w-full overflow-hidden">
				<Image
					src={project.coverImage}
					alt={project.name}
					height={220}
					aspectRatio={16 / 10}
					objectFit="cover"
					className="absolute inset-0 h-full w-full object-cover!"
				/>
			</div>
			<div className="flex flex-1 flex-col gap-4 p-6">
				{project.ngo?.logo && (
					<Image
						src={project.ngo.logo.location}
						alt={project.ngo.name ?? ''}
						height={28}
						aspectRatio={4}
						objectFit="contain"
						className="h-6 w-auto max-w-28 object-contain!"
					/>
				)}
				<h3 className="line-clamp-2 text-lg font-semibold">{project.name}</h3>
				<div className="mt-auto flex flex-col gap-2">
					{/* Whole euros: `formatNumber` defaults to two decimals, and a trailing ",00" on every
					    card is noise at this size. */}
					<p className="text-sm">
						<span className="text-brand-strong font-semibold">{formatNumber(language, raised, 0)} €</span>
						{goal ? (
							<span className="text-ink/50">
								{' '}
								{t`of`} {formatNumber(language, goal, 0)} €
							</span>
						) : null}
					</p>
					{pct !== undefined && (
						<div className="bg-ink/10 h-1 overflow-hidden rounded-full">
							<div className="bg-brand h-full rounded-full" style={{ width: `${pct}%` }} />
						</div>
					)}
				</div>
			</div>
		</article>
	);
}

/** "ONGs y sus proyectos más recientes" — real recent projects from our system, in a carousel. */
export function RecentProjects({ projects, ngoCount }: { projects: RecentProject[]; ngoCount: number }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const scrollerRef = useRef<HTMLDivElement>(null);

	const scrollByCards = (dir: 1 | -1) => {
		scrollerRef.current?.scrollBy({ left: dir * 360, behavior: 'smooth' });
	};

	if (projects.length === 0) return null;

	// lingui disallows function calls inside t`` — format the count into a variable first.
	const ngoCountLabel = formatNumber(language, ngoCount, 0);

	return (
		<section className="mx-auto max-w-[1440px] px-6 py-16 md:py-24 xl:px-10">
			<div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
				<Eyebrow>{t`Partner NGOs`}</Eyebrow>
				<h2 className="text-3xl font-medium tracking-tight text-balance md:text-5xl">
					{t`NGOs and their most recent projects`}
				</h2>
				<p className="text-ink/70 max-w-2xl text-base md:text-lg">
					{t`We collaborate with ${ngoCountLabel} NGOs committed to social, environmental and humanitarian causes. Each one passes a strict selection and audit process to ensure the funds turn into real solutions.`}
				</p>
				<Link
					to="/new/partner-ngos"
					className="bg-brand hover:bg-brand-strong mt-2 flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-black transition-colors"
				>
					{t`See partner NGOs`}
					<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
						<ArrowUpRight className="size-4" />
					</span>
				</Link>
			</div>

			<div ref={scrollerRef} className="hide-scrollbar mt-12 flex snap-x gap-6 overflow-x-auto scroll-smooth pb-2">
				{projects.map((project) => (
					<ProjectCard key={project.id} project={project} />
				))}
			</div>

			<div className="mt-8 flex justify-center gap-3">
				<button
					type="button"
					onClick={() => scrollByCards(-1)}
					aria-label={t`Previous`}
					className="border-ink/20 hover:bg-ink/5 grid size-11 place-items-center rounded-full border transition-colors"
				>
					<ArrowLeft className="size-5" />
				</button>
				<button
					type="button"
					onClick={() => scrollByCards(1)}
					aria-label={t`Next`}
					className="border-ink/20 hover:bg-ink/5 grid size-11 place-items-center rounded-full border transition-colors"
				>
					<ArrowRight className="size-5" />
				</button>
			</div>
		</section>
	);
}
